import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  submitShopInquiryFromCart,
  openShopInquiryWhatsAppFromCart,
} from '../utils/shopInquirySubmit';
import { useShopLanguage } from '../context/ShopLanguageContext';
import { cn } from '../lib/utils';
import {
  mapPublicInventoryToPickerInventory,
  buildInventoryById,
  wholesaleLinesToShopCart,
} from '../utils/shopWholesaleBasket';
import ShopWholesaleBasketForm from './ShopWholesaleBasketForm';
import ShopToaster from './ShopToaster';
import ShopOrderFlowOverlay from './ShopOrderFlowOverlay';

const getPublicInventory = async () => {
  const response = await fetch('/api/inventory/public');
  if (!response.ok) throw new Error('Failed to fetch inventory');
  return response.json();
};

const ShopWholesaleBasketPage = () => {
  const navigate = useNavigate();
  const { locale, setLocale, t } = useShopLanguage();
  const [loadingInventory, setLoadingInventory] = useState(true);
  const [publicInventory, setPublicInventory] = useState([]);
  const [cart, setCart] = useState([]);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    customerName: '',
    phone: '',
    deliveryDate: '',
  });
  const [submittingInquiry, setSubmittingInquiry] = useState(false);
  const [orderFlowPhase, setOrderFlowPhase] = useState(null);
  const [completedInquiryResult, setCompletedInquiryResult] = useState(null);

  const itemName = useCallback(
    (item) => {
      if (locale === 'mr' && item?.modelNameMr) return item.modelNameMr;
      if (locale === 'kn' && item?.modelNameKn) return item.modelNameKn;
      return item?.modelName;
    },
    [locale]
  );

  const pickerInventory = useMemo(
    () => mapPublicInventoryToPickerInventory(publicInventory),
    [publicInventory]
  );
  const inventoryById = useMemo(() => buildInventoryById(publicInventory), [publicInventory]);

  useEffect(() => {
    getPublicInventory()
      .then(setPublicInventory)
      .catch(() => toast.error(t('toast.loadModelsFailed')))
      .finally(() => setLoadingInventory(false));
  }, [t]);

  useEffect(() => {
    document.documentElement.classList.add('overflow-hidden');
    document.body.classList.add('overflow-hidden');
    return () => {
      document.documentElement.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden');
    };
  }, []);

  const handleProceedToCheckout = (lines) => {
    const shopCart = wholesaleLinesToShopCart(lines, inventoryById);
    if (shopCart.length === 0) {
      toast.error(t('wholesaleBasket.emptyBucket'));
      return;
    }
    setCart(shopCart);
    setCheckoutForm({
      customerName: '',
      phone: '',
      deliveryDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    });
    setShowCheckoutModal(true);
  };

  const prepareInquiryCart = () => {
    if (!checkoutForm.customerName.trim()) {
      toast.error(t('toast.nameRequired'));
      return null;
    }
    if (!checkoutForm.phone.trim() || !/^[6-9]\d{9}$/.test(checkoutForm.phone.trim())) {
      toast.error(t('toast.phoneRequired'));
      return null;
    }
    return {
      updatedCart: cart.map((cartItem) => ({
        ...cartItem,
        customerName: checkoutForm.customerName,
        phone: checkoutForm.phone,
        deliveryDate: checkoutForm.deliveryDate,
        advanceAmount: 0,
      })),
    };
  };

  const handleInquirySubmit = async () => {
    const prepared = prepareInquiryCart();
    if (!prepared) return;
    const { updatedCart } = prepared;
    try {
      setSubmittingInquiry(true);
      setOrderFlowPhase('inquirySubmitting');
      const inquiry = await submitShopInquiryFromCart({
        cart: updatedCart,
        customerName: checkoutForm.customerName,
        phone: checkoutForm.phone,
        locale,
        orderType: 'wholesale',
      });
      setCompletedInquiryResult({
        inquiry,
        cart: updatedCart,
        customerName: checkoutForm.customerName,
        phone: checkoutForm.phone,
        deliveryDate: checkoutForm.deliveryDate,
      });
      setCart([]);
      setShowCheckoutModal(false);
      setOrderFlowPhase('inquirySuccess');
    } catch (error) {
      setOrderFlowPhase(null);
      setCompletedInquiryResult(null);
      toast.error(error.message || t('toast.inquiryFailed'));
    } finally {
      setSubmittingInquiry(false);
    }
  };

  const resetOrderFlow = () => {
    setOrderFlowPhase(null);
    setCompletedInquiryResult(null);
    setShowCheckoutModal(false);
  };

  const handleContinueAfterFlow = () => {
    resetOrderFlow();
    navigate('/');
  };

  const handleOpenInquiryWhatsApp = () => {
    if (!completedInquiryResult) return;
    openShopInquiryWhatsAppFromCart({
      inquiry: completedInquiryResult.inquiry,
      cart: completedInquiryResult.cart,
      customerName: completedInquiryResult.customerName,
      phone: completedInquiryResult.phone,
      deliveryDate: completedInquiryResult.deliveryDate,
      itemName,
      orderType: 'wholesale',
      t,
    });
  };

  return (
    <>
      <ShopToaster />
      <ShopOrderFlowOverlay
        phase={orderFlowPhase}
        orderResult={null}
        inquiryResult={completedInquiryResult}
        onContinue={handleContinueAfterFlow}
        onOpenWhatsApp={
          completedInquiryResult ? handleOpenInquiryWhatsApp : undefined
        }
        t={t}
      />

      <div className="flex h-[100dvh] min-h-0 flex-col bg-gradient-to-br from-background to-muted/30">
        <header className="shrink-0 border-b border-border bg-card/95 px-4 py-3 shadow-sm backdrop-blur-sm sm:px-6">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
                <img src="/logo.png" alt={t('header.logoAlt')} className="h-10 w-10 shrink-0 sm:h-12 sm:w-12" />
                <div className="min-w-0">
                  <h1 className="font-heading truncate text-lg font-semibold text-primary sm:text-xl">
                    {t('wholesaleBasket.title')}
                  </h1>
                  <p className="truncate text-xs text-muted-foreground sm:text-sm">{t('wholesaleBasket.subtitle')}</p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              <Link
                to="/"
                className="rounded-full border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted sm:px-4 sm:text-sm"
              >
                {t('nav.retailShop')}
              </Link>
              <div
                role="group"
                aria-label={t('lang.toggleAria')}
                className="inline-flex items-center rounded-xl border border-border bg-muted/60 p-0.5"
              >
                {(['en', 'mr', 'kn']).map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setLocale(code)}
                    aria-pressed={locale === code}
                    className={cn(
                      'rounded-lg px-2 py-1 text-xs font-semibold transition-colors',
                      locale === code
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted'
                    )}
                  >
                    {t(`lang.${code === 'en' ? 'enShort' : code === 'mr' ? 'mrShort' : 'knShort'}`)}
                  </button>
                ))}
              </div>
              </div>
          </div>
        </header>

        <main className="mx-auto flex min-h-0 w-full max-w-6xl flex-1 flex-col px-4 py-3 sm:px-6 sm:py-4">
          {loadingInventory ? (
            <div className="flex flex-1 items-center justify-center text-muted-foreground">
              {t('wholesaleBasket.loading')}
            </div>
          ) : (
            <ShopWholesaleBasketForm
              inventory={pickerInventory}
              t={t}
              onProceedToCheckout={handleProceedToCheckout}
              loading={submittingInquiry}
            />
          )}
        </main>
      </div>

      {showCheckoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 p-2 backdrop-blur-sm sm:p-4">
          <div className="max-h-[95vh] w-full max-w-md overflow-y-auto rounded-xl border border-border bg-card text-card-foreground shadow-2xl">
            <div className="p-6">
              <div className="mb-6 flex items-start justify-between">
                <h2 className="pr-2 text-xl font-bold text-foreground">{t('checkoutModal.title')}</h2>
                <button
                  type="button"
                  onClick={() => setShowCheckoutModal(false)}
                  className="text-xl text-muted-foreground hover:text-foreground"
                >
                  ✕
                </button>
              </div>
              <div className="mb-6 rounded-lg bg-muted p-4">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold">{t('checkoutModal.summaryTitle')}</h3>
                  <span className="inline-flex rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold capitalize text-purple-800">
                    {t('checkoutModal.orderTypeWholesale')}
                  </span>
                </div>
                <p className="mb-3 text-xs text-muted-foreground">{t('checkoutModal.wholesaleInquiryHint')}</p>
                <div className="space-y-2">
                  {cart.map((cartItem, index) => (
                    <div key={index} className="text-sm">
                      {itemName(cartItem)} × {cartItem.quantity}
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">{t('checkoutModal.fullName')}</label>
                  <input
                    type="text"
                    value={checkoutForm.customerName}
                    onChange={(e) => setCheckoutForm((prev) => ({ ...prev, customerName: e.target.value }))}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    placeholder={t('checkoutModal.namePlaceholder')}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">{t('checkoutModal.phone')}</label>
                  <input
                    type="tel"
                    value={checkoutForm.phone}
                    onChange={(e) => setCheckoutForm((prev) => ({ ...prev, phone: e.target.value }))}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    placeholder={t('checkoutModal.phonePlaceholder')}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">{t('checkoutModal.deliveryDate')}</label>
                  <input
                    type="date"
                    value={checkoutForm.deliveryDate}
                    onChange={(e) => setCheckoutForm((prev) => ({ ...prev, deliveryDate: e.target.value }))}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleInquirySubmit}
                  disabled={submittingInquiry}
                  className="w-full rounded-full bg-accent px-6 py-3 font-bold text-accent-foreground shadow-sm hover:bg-accent/90 disabled:opacity-50"
                >
                  {t('checkoutModal.submitInquiry')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShopWholesaleBasketPage;
